import { Link } from 'react-router';

import { Breadcrumb } from '@shared/chrome/Breadcrumb';
import { formatNumber } from '@shared/format';
import { Alert } from '@shared/ui/Alert';
import { EmptyState } from '@shared/ui/EmptyState';

import { copy } from '../_shared/copy';
import { settingsPathFor } from '../_shared/services';
import { useAdministeredSections } from '../_shared/useAdministeredSections';

/**
 * `/settings` — the service index.
 *
 * **THIS PAGE READS NOTHING, AND THAT IS THE POINT OF THE SPLIT.** `/settings`
 * used to render every service's table at once: eight sections, 240 rows and
 * 23 653 px of page on develop, behind eight parallel reads fired on arrival —
 * for a surface where an operator came to change one key in one service. The
 * index is now a list of the services this account administers, each one a link;
 * the read happens on the page that shows the table, and only for the service
 * whose page is open.
 *
 * The entries come from the session's capabilities rather than from probing each
 * service, so an account that administers three services sees three entries
 * rather than nine cards and six refusals.
 *
 * What the index deliberately does not carry: **a key count per service** (it
 * would cost exactly the nine reads the split removed, and a stale count is
 * worse than none), **a search across keys** (the keys live behind the links,
 * and a search that has not read them cannot find them), and **any editing
 * control** — a write belongs on the page that shows what is being written.
 */
export function SettingsIndexPage() {
  const sections = useAdministeredSections();

  return (
    <>
      <Breadcrumb items={[{ label: copy.crumbs.console, to: '/' }, { label: copy.crumb }]} />

      <div className="mb-6 mt-3 grid gap-stack-tight">
        <h1 className="font-display text-h1 font-bold">{copy.head.title}</h1>
        <p className="mb-0 max-w-measure text-[1.0625rem] text-ink-muted">{copy.head.lede}</p>
      </div>

      {/* A statement of the surface's own boundary — it carries no alert role. */}
      <Alert kind="info" title={copy.contract.title} className="mb-8">
        {copy.contract.body} <bdi>{copy.contract.counts(formatNumber(sections.length))}</bdi>
      </Alert>

      <section aria-labelledby="settings-services">
        <h2 id="settings-services" className="mb-4 font-display text-h2 font-bold">
          {copy.index.heading}
        </h2>

        {sections.length === 0 ? (
          <EmptyState message={copy.index.empty} />
        ) : (
          <ul className="m-0 grid list-none gap-4 p-0 md:grid-cols-2">
            {sections.map((spec) => (
              <li key={spec.key} className="m-0">
                {/*
                  The whole card is the link, so the hit area is the card rather
                  than a word inside it — and there is exactly one link per card,
                  which is what keeps a keyboard reader from tabbing through two
                  controls that go to the same place.
                */}
                <Link
                  to={settingsPathFor(spec.key)}
                  aria-label={`${spec.heading} — ${copy.index.open}`}
                  className="flex h-full flex-col gap-2 rounded-md border border-border bg-surface p-5 no-underline hover:border-border-strong hover:bg-hover-bg"
                >
                  <span className="font-display text-h3 font-semibold text-ink">{spec.heading}</span>
                  <bdi className="mono text-xs text-ink-muted">{spec.table}</bdi>
                  <span className="text-sm text-ink-muted">{spec.context}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}

        <p className="mb-0 mt-4 max-w-measure text-xs text-ink-muted">{copy.index.unread}</p>
      </section>

      {/* Why three repos have no entry — named, not left to be noticed. */}
      <p className="mb-0 mt-section-gap max-w-measure text-xs text-ink-muted">{copy.absence}</p>
    </>
  );
}
